/**
 * Allowlist of remote-control commands the bridge accepts from the
 * parent window. The set is the union of:
 *   - every `action` exposed by the LG on-screen virtual remote
 *     (lib/sauron_web/components/remote_controller/lg.html.heex), so
 *     anything a user can click in the embed UI is also reachable
 *     from postMessage;
 *   - the digit keys 0-9 that the keyboard LOOKUP table in Webrtc.js
 *     produces (not on the virtual remote).
 *
 * Server-side `Sauron.Devices.Session.Input` accepts an arbitrary
 * string for `command`, so this allowlist is the primary protection
 * against a compromised parent smuggling junk into
 * `Devices.create_input/2`. Keep it in sync with the LG remote's
 * action set when adding a new on-screen button.
 */
const ALLOWED_REMOTE_COMMANDS = new Set([
  // Navigation
  "ok",
  "up",
  "down",
  "left",
  "right",
  "back",
  "home",
  "menu",
  "top_menu",
  // System / input
  "power",
  "stb-power",
  "input",
  "settings",
  "options",
  "info",
  "guide",
  "display",
  "time",
  // Audio
  "audio",
  "mute",
  "volume_up",
  "volume_down",
  "voice",
  // Channels
  "ch_next",
  "ch_prev",
  // Color keys (used heavily by TV apps for in-app navigation)
  "red",
  "green",
  "yellow",
  "blue",
  // Media transport
  "play",
  "pause",
  "play_pause",
  "stop",
  "rewind",
  "fast_forward",
  "next",
  "prev",
  "eject",
  // Disc-changer controls (legacy on the LG remote but supported by
  // some hardware; cheap to allow alongside the rest)
  "load_disc",
  "next_disc",
  "prev_disc",
  "group_disc",
  "sort_disc",
  // Numeric keys (keyboard 0-9; not on the on-screen LG remote)
  "number-0",
  "number-1",
  "number-2",
  "number-3",
  "number-4",
  "number-5",
  "number-6",
  "number-7",
  "number-8",
  "number-9",
]);

/**
 * UI actions a native-chrome embedder (Device IO desktop) may trigger via
 * `{type:"embed-action"}`. The parent is untrusted, so — like
 * ALLOWED_REMOTE_COMMANDS — this is the security boundary: only these explicit
 * actions reach `pushEvent`, never an arbitrary handler (e.g. execute_snippet).
 * Each mirrors a control the cockpit already exposes; keep it in sync.
 */
const ALLOWED_EMBED_ACTIONS = new Set([
  "screen_capture",
  "toggle_theater_mode",
  "toggle_info",
  "toggle_settings",
  "toggle_keyboard_pass_through",
  "toggle_stats",
  "open_build_selector",
  "teleport_to_region",
  "toggle_remote_panel",
  // Native toolbar volume control (slider + mute). update_volume takes the
  // {volume} payload (0-100 string); toggle_mute takes none.
  "update_volume",
  "toggle_mute",
  // Native window close → end the session cleanly server-side (deterministic
  // teardown instead of waiting for the socket to drop). It's the embedder's
  // own session, so this is within its rights.
  "end_session",
]);

/**
 * Dispatch the `remote_control` CustomEvent shape that the Webrtc
 * hook's `handleRemoteButtonPress` listener consumes — same wire
 * format as the on-screen virtual remote (see RemoteButton.js).
 */
function dispatchRemoteEvent(target, command, event, repeat) {
  target.dispatchEvent(
    new CustomEvent("remote_control", {
      detail: { command, event, repeat },
    }),
  );
}

/**
 * Whether `event` is the key the embedder asked to take the keyboard back
 * with, `search` being the frame's query string (`keyboard_release=escape`).
 */
export function isKeyboardRelease(event, search) {
  if (!event || event.key !== "Escape") return false;
  return new URLSearchParams(search || "").get("keyboard_release") === "escape";
}

/**
 * Implements a PostMessage API for embedded routes (VS Code iframe)
 *
 * Allows for communicating from VS Code to TV Labs
 */
export const EmbedPostMessageBridge = {
  mounted() {
    this.parentOrigin = null;
    this.applyThemeFromURL();
    this.setupPostMessageListener();
    this.setupLiveViewEventListeners();
    this.notifySessionIfReady();
    this.setupStreamMeta();
  },

  updated() {
    this.notifySessionIfReady();
    this.setupStreamMeta();
  },

  setupLiveViewEventListeners() {
    // The LiveView pushes "session-ended" when the user clicks the
    // hang-up button (or any other server-driven session termination
    // path that runs through the embed view). We forward it to the
    // parent window so the embedder — typically the VS Code extension
    // — can dispose of the iframe and run its own teardown cascade.
    this.handleEvent("session-ended", (payload) => {
      this.postToParent({
        type: "session-ended",
        session_id: payload && payload.session_id,
      });
    });

    // Fan out per-screenshot capture events to the parent window so
    // embedders can render a live thumbnail strip without polling
    // /api/v1/sessions/:id/screenshots. Sauron's ScreenshotConsumer
    // still owns the persisted Screenshot row; this signal fires the
    // instant the asset_uploaded event lands (well before the batch
    // commit), so the embedder gets the URL with the lowest possible
    // latency.
    //
    // Server payload shape (from AccessLive.Show):
    //   - session_id  : string  — uuid of the session the asset belongs to
    //   - url         : string  — presigned R2 URL of the captured image
    //   - label       : string? — user-supplied caption or nil
    //   - captured_at : string  — ISO 8601 timestamp at which the LV emitted it
    this.handleEvent("screenshot_captured", (payload) => {
      // Defensive: drop malformed payloads rather than postMessaging
      // garbage. Phoenix doesn't guarantee single-delivery on the
      // embed channel and a flaky reconnect could in principle replay
      // an empty frame; the embedder should never see a screenshot
      // event without a usable url.
      if (!payload?.url || typeof payload.url !== "string") {
        return;
      }
      this.postToParent({
        type: "screenshot-captured",
        session_id: payload.session_id,
        url: payload.url,
        label: payload.label,
        captured_at: payload.captured_at,
      });
    });
  },

  notifySessionIfReady() {
    const sessionId = this.el.dataset.sessionId;
    if (sessionId && sessionId !== this._lastNotifiedSessionId) {
      this._lastNotifiedSessionId = sessionId;
      this.postToParent({ type: "session-ready", session_id: sessionId });
    }
  },

  applyThemeFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const themeBg = urlParams.get("theme_bg");
    const themeBorder = urlParams.get("theme_border");
    const themeHover = urlParams.get("theme_hover");
    const themeIcon = urlParams.get("theme_icon");

    if (themeBg || themeBorder || themeHover || themeIcon) {
      this.applyThemeColors({
        bg: themeBg,
        border: themeBorder,
        hover: themeHover,
        icon: themeIcon,
      });
    }
  },

  postToParent(message) {
    if (window.parent !== window) {
      const origin = this.parentOrigin || "*";
      window.parent.postMessage(message, origin);
    }
  },

  setupPostMessageListener() {
    this.messageHandler = (event) => {
      if (!event || !event.data) {
        return;
      }
      // Latch the parent origin from the first inbound message of
      // any type, so subsequent `postToParent` calls target it
      // explicitly instead of falling back to "*". Previously this
      // only ran on theme-update (the only inbound type at the
      // time); the broader latch is required now that control
      // messages (take-screenshot / remote-press) may arrive
      // first.
      if (this.parentOrigin === null && event.origin) {
        this.parentOrigin = event.origin;
      }

      if (event.data.type === "theme-update") {
        const colors = event.data.colors;
        this.applyThemeColors(colors);
        return;
      }

      // Parent-origin control commands. Used by the VS Code extension's
      // MCP bridge to drive the device from an AI agent. Each command
      // shape is intentionally minimal — we don't accept arbitrary
      // LiveView events to avoid turning the embed into an open RPC
      // surface; only the explicit allowlist below is honored.
      if (event.data.type === "take-screenshot") {
        this.handleTakeScreenshot(event.data);
        return;
      }
      if (event.data.type === "remote-press") {
        this.handleRemotePress(event.data);
        return;
      }
      // Native-chrome embedders (Device IO desktop) draw the session toolbar
      // themselves and forward a UI action by name; it drives the same
      // `handle_event` a click would. The server's clauses are the authority —
      // unknown actions are ignored there, so there's no client allowlist.
      if (event.data.type === "embed-action") {
        this.handleEmbedAction(event.data);
        return;
      }
    };

    window.addEventListener("message", this.messageHandler);

    // An embedder that opened the frame with `keyboard_release=escape` takes
    // the keyboard back on Esc. The capture listener runs before the video
    // player's document listener, which maps Esc to Back and, with keyboard
    // pass-through on, forwards every key, so Esc reaches neither and no key
    // is sent to the TV.
    this.keyboardReleaseHandler = (event) => {
      if (!isKeyboardRelease(event, window.location.search)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (event.type === "keydown") this.postToParent({ type: "keyboard-released" });
    };
    window.addEventListener("keydown", this.keyboardReleaseHandler, true);
    window.addEventListener("keyup", this.keyboardReleaseHandler, true);
  },

  /**
   * Forward an embedder-requested UI action to the LiveView. Validates against
   * ALLOWED_EMBED_ACTIONS so an untrusted parent can't reach an arbitrary
   * handler, coerces a missing/!object payload to {}, and pushes it through the
   * embed LiveView's normal `handle_event` path.
   */
  handleEmbedAction(data) {
    const action = data && data.action;
    if (typeof action !== "string" || !ALLOWED_EMBED_ACTIONS.has(action)) {
      return;
    }
    if (typeof this.pushEvent !== "function") {
      return;
    }
    const payload = data.payload && typeof data.payload === "object" ? data.payload : {};
    this.pushEvent(action, payload);
  },

  /**
   * Post `stream-meta` to the parent as soon as the video knows its intrinsic
   * dimensions, so a native-chrome embedder can lock its TV frame / window to
   * the real stream aspect instead of guessing 16:9. Idempotent across
   * `updated()` (the <video> re-renders when the session assign flips) and
   * fires immediately if metadata already arrived.
   */
  setupStreamMeta() {
    const video = document.getElementById("device-stream-video");
    if (!video || video === this._meteredVideo) {
      return;
    }
    this._meteredVideo = video;
    const emit = () => {
      if (!video.videoWidth || !video.videoHeight) {
        return;
      }
      this.postToParent({
        type: "stream-meta",
        videoWidth: video.videoWidth,
        videoHeight: video.videoHeight,
      });
    };
    this._onLoadedMetadata = emit;
    video.addEventListener("loadedmetadata", emit);
    if (video.readyState >= 1) {
      emit();
    }
  },

  /**
   * Trigger a screenshot capture exactly as if the user clicked the
   * camera button or pressed the `c` hotkey. The existing
   * `screen_capture` LiveView handler does the rest, eventually
   * pushing the `screenshot_captured` event back through the bridge
   * once the asset is uploaded — the parent correlates the request
   * with the resulting URL by listening for that event.
   */
  handleTakeScreenshot(_data) {
    if (typeof this.pushEvent !== "function") {
      // Hook hasn't completed mount yet — drop the request rather
      // than crashing. The parent should retry once it receives the
      // session-ready signal.
      return;
    }
    this.pushEvent("screen_capture", {});
  },

  /**
   * Send a remote-control button press into the device. The actual
   * key transmission goes through the WebRTC data channel owned by
   * the Webrtc hook on `#device-stream`; we re-use the existing
   * `remote_control` CustomEvent path that the on-screen virtual
   * remote uses (RemoteButton.js → handleRemoteButtonPress) so this
   * doesn't introduce a second control plane.
   *
   * Payload: { command: string, event?: "keydown" | "keyup" | "tap" }
   *   - "tap" (default) sends a paired keydown + keyup with a small
   *     delay between them, mimicking a real button press.
   *   - "keydown" / "keyup" let the agent hold a button (e.g. for
   *     long-press scenarios) by sending the events explicitly.
   *
   * We only forward known commands from a fixed allowlist so a
   * compromised parent can't smuggle arbitrary strings through to
   * `Devices.create_input/2`.
   */
  handleRemotePress(data) {
    const command = data && data.command;
    if (typeof command !== "string" || !ALLOWED_REMOTE_COMMANDS.has(command)) {
      return;
    }
    const target = document.getElementById("device-stream");
    if (!target) {
      // No active session yet (queue screen). Drop silently — parent
      // can poll session state via tvlabs_get_active_session before
      // issuing presses.
      return;
    }
    const eventType = data.event;
    if (eventType === "keydown" || eventType === "keyup") {
      dispatchRemoteEvent(target, command, eventType, false);
      return;
    }
    // Default: tap = keydown + keyup with a short delay so devices
    // that debounce on hold (most TVs) register it as a real press.
    dispatchRemoteEvent(target, command, "keydown", false);
    setTimeout(() => dispatchRemoteEvent(target, command, "keyup", false), 50);
  },

  applyThemeColors(colors) {
    const root = document.documentElement;

    if (colors.bg) {
      root.style.setProperty("--embed-bg", `#${colors.bg}`);
    }

    if (colors.border) {
      root.style.setProperty("--embed-border", `#${colors.border}`);
    }

    if (colors.hover) {
      root.style.setProperty("--embed-hover", `#${colors.hover}`);
    }

    if (colors.icon) {
      root.style.setProperty("--embed-icon", `#${colors.icon}`);
    }
  },

  destroyed() {
    if (this.messageHandler) {
      window.removeEventListener("message", this.messageHandler);
    }
    if (this.keyboardReleaseHandler) {
      window.removeEventListener("keydown", this.keyboardReleaseHandler, true);
      window.removeEventListener("keyup", this.keyboardReleaseHandler, true);
    }
    if (this._meteredVideo && this._onLoadedMetadata) {
      this._meteredVideo.removeEventListener("loadedmetadata", this._onLoadedMetadata);
    }
  },
};
